import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CaretLeft, CaretRight, Lock } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Logo from '../components/Logo';
import { formatMoney } from '../utils/currency';

const monthNames = [
  'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez',
];

const colors = ['#3b82f6', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316', '#6366f1'];

export default function YearSummary() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [blocked, setBlocked] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/expenses/year/${year}`);
      setData(res.data);
      setBlocked(false);
    } catch (err) {
      if (err.response?.data?.upgradeRequired) {
        setBlocked(true);
      }
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { loadData(); }, [loadData]);

  const maxTotal = data ? Math.max(...data.months.map(m => m.total), 1) : 1;

  // Top categorias do ano
  const yearCats = {};
  if (data) {
    data.months.forEach(m => {
      Object.entries(m.categories).forEach(([cat, val]) => {
        yearCats[cat] = (yearCats[cat] || 0) + val;
      });
    });
  }
  const sortedCats = Object.entries(yearCats).sort(([,a], [,b]) => b - a);
  const maxCat = sortedCats.length > 0 ? sortedCats[0][1] : 1;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/50">
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/60 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Logo linkTo="/dashboard" />
          <Link
            to="/dashboard"
            className="text-sm text-slate-400 hover:text-slate-600 transition flex items-center gap-1.5"
          >
            <CaretLeft size={16} />
            <span className="hidden sm:inline">Voltar ao dashboard</span>
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {blocked ? (
          <div className="text-center py-20">
            <Lock size={64} className="text-amber-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">Recurso do plano Pro</h2>
            <p className="text-slate-400 mb-6">O resumo anual esta disponivel no plano Pro.</p>
            <button
              onClick={() => navigate('/account?tab=plans')}
              className="px-6 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition"
            >
              Ver planos
            </button>
          </div>
        ) : (
        <>
        {/* Header */}
        <div className="flex items-center justify-between mb-6 sm:mb-8">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800">Resumo anual</h1>
            <p className="text-slate-400 text-sm mt-1">Visao geral dos seus gastos</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setYear(y => y - 1)}
              className="p-2 rounded-lg hover:bg-white hover:shadow-sm transition text-slate-400 border border-transparent hover:border-slate-200"
            >
              <CaretLeft size={16} />
            </button>
            <span className="text-lg font-semibold text-slate-800 min-w-[60px] text-center">{year}</span>
            <button
              onClick={() => setYear(y => y + 1)}
              className="p-2 rounded-lg hover:bg-white hover:shadow-sm transition text-slate-400 border border-transparent hover:border-slate-200"
            >
              <CaretRight size={16} />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <svg className="w-6 h-6 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
        ) : data && (
          <>
            {/* Cards resumo */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
              <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-slate-200/60">
                <p className="text-[11px] sm:text-xs font-medium text-slate-400 mb-1">Total do ano</p>
                <p className="text-lg sm:text-2xl font-bold text-slate-800 truncate">{formatMoney(data.yearTotal, user)}</p>
              </div>
              <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-slate-200/60">
                <p className="text-[11px] sm:text-xs font-medium text-slate-400 mb-1">Media mensal</p>
                <p className="text-lg sm:text-2xl font-bold text-slate-800 truncate">{formatMoney(data.average, user)}</p>
              </div>
              <div className="col-span-2 lg:col-span-1 bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-slate-200/60">
                <p className="text-[11px] sm:text-xs font-medium text-slate-400 mb-1">Mes mais caro</p>
                <p className="text-lg sm:text-2xl font-bold text-rose-500 truncate">
                  {(() => {
                    const max = data.months.reduce((a, b) => a.total > b.total ? a : b);
                    return max.total > 0 ? `${monthNames[max.month - 1]} - ${formatMoney(max.total, user)}` : '--';
                  })()}
                </p>
              </div>
            </div>

            {/* Grafico de barras por mes */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 p-4 sm:p-6 mb-6 sm:mb-8">
              <h3 className="font-semibold text-slate-800 mb-4 sm:mb-6">Gastos por mes</h3>
              <div className="flex items-end gap-1 sm:gap-2 h-48 overflow-x-auto">
                {data.months.map((m) => {
                  const height = maxTotal > 0 ? (m.total / maxTotal) * 100 : 0;
                  return (
                    <div key={m.month} className="flex-1 min-w-[28px] flex flex-col items-center gap-1">
                      <span className="text-[8px] sm:text-[10px] font-medium text-slate-500 hidden sm:block">
                        {m.total > 0 ? formatMoney(m.total, user) : ''}
                      </span>
                      <div className="w-full flex items-end" style={{ height: '140px' }}>
                        <div
                          className="w-full bg-blue-500 rounded-t-md transition-all duration-500 hover:bg-blue-600"
                          style={{ height: `${Math.max(height, m.total > 0 ? 2 : 0)}%` }}
                        />
                      </div>
                      <span className="text-[9px] sm:text-[11px] font-medium text-slate-400">{monthNames[m.month - 1]}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Categorias do ano */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 p-4 sm:p-6">
                <h3 className="font-semibold text-slate-800 mb-4">Categorias do ano</h3>
                {sortedCats.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">Sem dados</p>
                ) : (
                  <div className="space-y-3">
                    {sortedCats.map(([cat, val], i) => (
                      <div key={cat}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-slate-600">{cat}</span>
                          <span className="text-xs font-medium text-slate-800">{formatMoney(val, user)}</span>
                        </div>
                        <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-700"
                            style={{ width: `${(val / maxCat) * 100}%`, backgroundColor: colors[i % colors.length] }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Tabela mensal */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 overflow-hidden">
                <div className="px-4 sm:px-6 py-4 border-b border-slate-100">
                  <h3 className="font-semibold text-slate-800">Detalhamento mensal</h3>
                </div>
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left text-xs font-medium text-slate-400 uppercase px-3 sm:px-6 py-2">Mes</th>
                      <th className="text-right text-xs font-medium text-slate-400 uppercase px-3 sm:px-6 py-2">Despesas</th>
                      <th className="text-right text-xs font-medium text-slate-400 uppercase px-3 sm:px-6 py-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.months.map((m) => (
                      <tr key={m.month} className="border-b border-slate-50 hover:bg-slate-50/60 transition">
                        <td className="px-3 sm:px-6 py-2.5 text-sm font-medium text-slate-700">{monthNames[m.month - 1]}</td>
                        <td className="px-3 sm:px-6 py-2.5 text-sm text-slate-500 text-right">{m.count}</td>
                        <td className="px-3 sm:px-6 py-2.5 text-sm font-semibold text-slate-800 text-right whitespace-nowrap">
                          {m.total > 0 ? formatMoney(m.total, user) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-slate-200 bg-slate-50/50">
                      <td className="px-3 sm:px-6 py-2.5 text-sm font-semibold text-slate-800">Total</td>
                      <td className="px-3 sm:px-6 py-2.5 text-sm text-slate-500 text-right">
                        {data.months.reduce((s, m) => s + m.count, 0)}
                      </td>
                      <td className="px-3 sm:px-6 py-2.5 text-sm font-bold text-slate-800 text-right whitespace-nowrap">
                        {formatMoney(data.yearTotal, user)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </>
        )}
        </>
        )}
      </main>
    </div>
  );
}
